import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { ordersAPI } from "../services/api";
import { useEffect } from "react";

interface DashboardStats {
  total_orders: number;
  pending_orders: number;
  approved_orders: number;
  in_transit_orders: number;
  completed_orders: number;
  total_revenue: number;
  team_performance: number;
  average_response_time: number;
  order_approval_rate: number;
  customer_satisfaction: number;
  // eski alanlar (isteğe bağlı):
  monthlyRevenue?: number;
  monthlyOrderCount?: number;
  newCustomerCount?: number;
  averageOrderAmount?: number;
  pendingOrders?: number;
  approvedOrders?: number;
  inTransitOrders?: number;
  deliveredOrders?: number;
}

interface RecentOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  salesperson: string;
  manager?: string;
  totalAmount: number;
  status: string;
  date: string;
  warehouse?: string;
  vehiclePlate?: string;
  items: any[];
  notes?: string;
}

interface UseDashboardDataReturn {
  stats: DashboardStats | null;
  recentOrders: RecentOrder[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDashboardData = (): UseDashboardDataReturn => {
  const { user } = useAuth();

  // Fetch dashboard stats
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['dashboard-stats', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('User not found');
      return await ordersAPI.getDashboardStats();
    },
    enabled: !!user,
    staleTime: 15 * 1000, // 15 seconds (daha kısa)
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds (daha sık)
    refetchOnWindowFocus: true, // Pencere odaklandığında güncelle
    refetchOnMount: true, // Component mount olduğunda güncelle
  });

  // Fetch recent orders
  const {
    data: recentOrders = [],
    isLoading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: ['recent-orders', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('User not found');
      const response = await ordersAPI.getRecentOrders(10);
      return response.map((order) => ({
        id: order.id || order._id,
        orderNumber: order.orderNumber || order.order_number,
        customerName: order.customerName || order.customer_name,
        salesperson: order.salesperson || order.salesperson_id?.full_name || '',
        manager: order.manager || order.manager_id?.full_name,
        totalAmount: order.totalAmount || order.total_amount,
        status: order.status,
        date: order.date || order.created_at,
        warehouse: order.warehouse || order.warehouse_id?.full_name,
        vehiclePlate: order.vehiclePlate || (order as any).vehicle_plate,
        items: order.items,
        notes: order.notes,
      }));
    },
    enabled: !!user,
    staleTime: 5 * 1000, // 5 seconds (daha kısa)
    refetchInterval: 15 * 1000, // Auto-refresh every 15 seconds (daha sık)
    refetchOnWindowFocus: true, // Pencere odaklandığında güncelle
    refetchOnMount: true, // Component mount olduğunda güncelle
  });

  // Sayfa görünür olduğunda ve odaklandığında otomatik güncelleme
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchStats();
        refetchOrders();
      }
    };

    const handleFocus = () => {
      refetchStats();
      refetchOrders();
    };

    // Event listener'ları ekle
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchStats, refetchOrders]);

  const refetch = async () => {
    await Promise.all([refetchStats(), refetchOrders()]);
  };

  return {
    stats,
    recentOrders,
    loading: statsLoading || ordersLoading,
    error: statsError?.message || ordersError?.message || null,
    refetch,
  };
}; 